import { useEffect, useState } from 'react'
import { signedShopUrl } from './shop'

/**
 * A signed URL for a stored shop image, or null.
 *
 * The bucket is PRIVATE (070), so nothing renders without one — the same
 * posture as post-media, and for a related reason: the shop is behind the
 * application login and a public bucket would put every product shot on the
 * open internet by URL.
 *
 * ── NULL MEANS BOTH "LOADING" AND "COULD NOT SIGN", DELIBERATELY ────────────
 *
 * Collapsing the two states is the right call here because nothing the reader
 * could do differs between them. A broken-image glyph tells somebody their
 * browser is faulty; an empty slot reads as "no picture", which is what they
 * should conclude either way. The manager screen says more, because there the
 * difference is actionable.
 *
 * It lives in `lib/` beside `useDismiss` rather than inside a component,
 * because both the shop floor and the admin pictures panel need it and a copy
 * in each is two places for the expiry to drift.
 */
export function useShopImage(path: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    if (!path) {
      setUrl(null)
      return
    }
    // Clear first: without this, changing colour shows the PREVIOUS colour's
    // photograph until the new signature comes back, which reads as the picker
    // having picked the wrong thing.
    setUrl(null)
    signedShopUrl(path)
      .then((u) => live && setUrl(u))
      .catch(() => live && setUrl(null))
    return () => {
      live = false
    }
  }, [path])

  return url
}
